import { create } from 'zustand'
import { SITE_HOST } from '../lib/brand'
import type {
  Branding,
  Decoration,
  LightsOsDocument,
  Layer,
  Point,
  ProductId,
  SceneSettings,
  Strand,
  StrandKind,
} from '../types/document'
import { createDocument, isStrand } from '../types/document'
import type { CustomLine, LineOverride, RateKey, SeasonTier } from '../types/pricing'
import type { QuoteLine } from '../lib/quote'
import { emptyLine } from '../lib/quote'
import type { Deposit, Paper, Payment } from '../lib/paper'
import { defaultPaper } from '../lib/paper'
import type { Company } from '../lib/company'
import { loadCompany, nextDocNumber, saveCompany } from '../lib/company'
import { loadDepositPref, saveDepositPref } from '../lib/depositPref'
import { applyTo, loadDefaults } from '../lib/defaults'
import { canEdit, loading } from '../lib/editable'
import type { MeasureSource, RoofSection, SectionKind } from '../lib/sections'
import { approvalBlockers, approvalsOf, approveScope } from '../lib/approval'
import { takeoff } from '../lib/materials'
import { PRODUCTS } from '../lib/products'
import { PACKAGES } from '../lib/packages'
import {
  attachRun as attachRunTo,
  createSection,
  detachRun as detachRunFrom,
  sectionsOf,
  setMeasurement,
  syncSections,
  verifyMeasurement,
} from '../lib/sections'
import { defaultRates } from '../types/pricing'
import { BULB_COLORS } from '../lib/palettes'
import { defaultStyleFor } from '../lib/decorLibrary'
import type { EdgeMap } from '../lib/edges'
import { straightenRun } from '../lib/straighten'
import type {
  OcclusionDecision,
  RoofProposal,
  SideReturnDecision,
} from '../types/proposal'
import type { Blocker } from '../lib/proposals'
import {
  CLIENT_MESSAGES,
  requestSuggestions,
  type SuggestOptions,
  type SuggestPhase,
} from '../lib/aiSuggestions'
import type { StrandTemplate } from '../lib/proposals'
import {
  confirmProposal as confirmProposalDoc,
  nextSeq,
  runLabel,
  countCorrection,
  editProposal,
  makeProposal,
  needsConfirmation,
  reconcile,
  removeProposal as removeProposalDoc,
  strandTemplate,
} from '../lib/proposals'

/**
 * The in-flight suggestion request, so Cancel has something to pull on.
 *
 * Module scope rather than store state: an AbortController is not data, it
 * does not belong in a snapshot, and undo must never resurrect one.
 */
let suggestAbort: AbortController | null = null

const ACCESS_CODE_KEY = 'lightsos.accessCode'

function readAccessCode(): string {
  try {
    return sessionStorage.getItem(ACCESS_CODE_KEY) ?? ''
  } catch {
    return ''
  }
}

export type Tool =
  | 'select'
  | 'roofline'
  /**
   * TWO CLICKS, TWO CIRCLES, A STRAIGHT LINE, AND NOTHING ELSE.
   *
   * The roofline tool slides what you drew onto the strongest architectural
   * line under it and then runs it out to that line's ends, which is right for
   * a long fascia and wrong for a short recessed edge: the recess is not the
   * strongest line near it, so the run steps onto the neighbor and then
   * overshoots both ends. This tool takes the two clicks literally.
   */
  | 'shortEdge'
  /** Tap twice along an edge and it follows that edge to the corner. */
  | 'auto'
  | 'freehand'
  | 'wrap'
  | 'swag'
  /** A strand folded in half, hung from a branch. Two rows of bulbs. */
  | 'cluster'
  /** Places a wreath where you tap. */
  | 'wreath'
  /** Places a garland segment where you tap. */
  | 'garlandArt'
  /** Places whichever accessory is selected - a bow today, more as they arrive. */
  | 'accessory'
  | 'calibrate'
  | 'pan'

const uid = () => Math.random().toString(36).slice(2, 10)

/* ------------------------------------------------------ lights, immediately */

/**
 * ADD RUNS THAT ARE ALREADY LIT.
 *
 * A mockup tool that draws a dashed line and waits is not showing anybody a
 * mockup. Every run that lands - drawn by hand, suggested by the model,
 * restored from a file - becomes a Strand on the spot and renders as bulbs.
 *
 * The step this replaces existed to keep geometry NOBODY AGREED TO out of a
 * price. That rule was load bearing while the app computed footage per run.
 * It no longer does: a quote is a short list by category that a person types,
 * so there is no computed quantity left for a wrong line to corrupt, and the
 * step was costing a press per run to protect a number that does not exist.
 *
 * IF PRICING EVER GOES BACK TO COMPUTING FOOTAGE PER RUN, THIS HAS TO BE
 * REVISITED. That is the condition the decision rests on, written down rather
 * than remembered.
 *
 * What survives is the part that protects the person rather than the invoice.
 * confirmProposal still refuses on its own terms - an unresolved side return,
 * an edge nothing in the photograph shows - so a run it will not take stays a
 * dashed suggestion instead of being forced through. Nobody is made to press a
 * button to discover that; they just see the one line that did not light.
 */
function addLit(
  doc: LightsOsDocument,
  list: RoofProposal[],
  template: StrandTemplate,
): LightsOsDocument {
  if (!list.length) return doc
  let n = nextSeq(doc)
  const numbered = list.map((p) => ({ ...p, seq: p.seq ?? n++ }))
  let next = countCorrection(
    { ...doc, proposals: [...doc.proposals, ...numbered] },
    { runsProposed: list.length },
  )
  const now = new Date().toISOString()
  for (const p of numbered) {
    const r = confirmProposalDoc(next, p.id, { now, strandId: uid(), template, name: 'Roof run' })
    if (r.ok && !r.noop) next = r.doc
  }
  return next
}

/**
 * A RUN STAYS LIT WHILE YOU MOVE IT.
 *
 * Every geometry edit withdraws a confirmation and deletes the Strand it
 * named. That is reconcile doing its job and it is not changing: a Strand
 * whose points disagree with its proposal is exactly the corruption the two
 * collections exist to make impossible.
 *
 * So the run is confirmed AGAIN, from the edited geometry, in the same update.
 * The withdrawal is real and lasts no frames. What the person sees is a line
 * of bulbs following their finger, which is the entire promise of the tool.
 *
 * It reuses the SAME Strand id, so selection, the layer list and React keys do
 * not churn sixty times a second during a drag, and it carries the old
 * Strand's own settings across, so dragging a red run does not hand it back
 * warm white.
 *
 * Only a run that WAS lit is relit. A dashed suggestion that got nudged stays
 * dashed, and a run confirmProposal refuses - a side return nobody has ruled
 * on - goes dark and stays dark, which is the one case where stopping is the
 * honest answer.
 */
function relight(
  before: LightsOsDocument,
  after: LightsOsDocument,
  proposalId: string,
): LightsOsDocument {
  const was = (before.proposals ?? []).find((p) => p.id === proposalId)
  if (!was || was.status !== 'confirmed' || !was.strandId) return after
  const old = before.layers.find((l) => l.id === was.strandId)
  if (!old || !isStrand(old)) return after
  const r = confirmProposalDoc(after, proposalId, {
    now: new Date().toISOString(),
    strandId: was.strandId,
    template: strandTemplate(old),
    name: old.name,
  })
  return r.ok && !r.noop ? r.doc : after
}

/**
 * What you are lighting, rather than which drawing tool you are holding.
 * Contractors think in rooflines and palms, not polylines, so the navigation
 * is organized this way and each choice sets the right tool and product.
 */
export type LightCategory =
  | 'roofline'
  | 'garland'
  | 'palms'
  | 'trees'
  | 'wreaths'
  | 'clusters'
  | 'columns'
  | 'accessories'
  | 'other'

export const CATEGORY_SETUP: Record<
  LightCategory,
  {
    tool: Tool
    product: ProductId
    label: string
    blurb: string
    noun: string
    /**
     * Keep taking points until the user says stop.
     *
     * Two clicks is right for a straight fascia run, but a garage door is a
     * shape - up one side, across the header, down the other - and making that
     * three separate runs means three sets of corners that never quite meet and
     * three lines on the quote for one piece of garland.
     */
    multi?: true
  }
> = {
  roofline: {
    tool: 'auto',
    product: 'C9',
    label: 'Roofline',
    blurb: 'Click each end of the run. Draw it roughly - it straightens itself onto the roof edge.',
    noun: 'Roofline',
  },
  garland: {
    tool: 'swag',
    product: 'mini',
    label: 'Garland',
    blurb: 'Click each corner - up one side of the garage, across the top, down the other. Tap the last corner again to finish.',
    noun: 'Garland',
    multi: true,
  },
  /*
   * PALMS AND TREES ARE TWO DIFFERENT JOBS.
   *
   * They shared one button, and one geometry cannot serve both. A palm is a
   * bare trunk you wrap and a crown of fronds you light - the fronds are most
   * of the labour and they are billed each. A shade tree has no crown to light:
   * it is a trunk plus however many branches you decide to run up, and every
   * one of those is its own length of wrap.
   *
   * Splitting them is also how they get priced correctly, since one is per
   * frond and the other is per foot of wrap.
   */
  palms: {
    tool: 'wrap',
    product: 'mini',
    label: 'Palms',
    blurb: 'Click the bottom of the trunk, then the top. Switch to Fronds to trace the leaves.',
    noun: 'Palm',
  },
  trees: {
    tool: 'wrap',
    product: 'mini',
    label: 'Trees',
    blurb: 'Wrap the trunk first, then draw each branch you go up as its own run.',
    noun: 'Tree',
  },
  clusters: {
    tool: 'cluster',
    product: 'mini',
    label: 'Hanging clusters',
    blurb: 'Click where it hangs from, then where it ends. A strand folded in half, so it hangs as two rows.',
    noun: 'Cluster',
  },
  wreaths: {
    tool: 'wreath',
    product: 'mini',
    label: 'Wreaths',
    blurb: 'Click to place one. Size it below.',
    noun: 'Wreath',
  },
  columns: {
    tool: 'wrap',
    product: 'mini',
    label: 'Columns',
    blurb: 'Click the bottom of the column, then the top.',
    noun: 'Column',
  },
  /*
   * Accessories are anything that is simply PLACED rather than run along a
   * line: a bow on a wreath, and whatever else gets photographed next. The
   * category lists every product of that sort in the library, so a new one
   * appears here the moment its cutout exists - no code change to add a
   * reindeer.
   */
  accessories: {
    tool: 'accessory',
    product: 'mini',
    label: 'Accessories',
    blurb: 'Pick one below, then click where it goes.',
    noun: 'Accessory',
  },
  other: {
    tool: 'freehand',
    product: 'mini',
    label: 'Bushes & shrubs',
    blurb: 'Press and drag over the bush. Follow its shape and the lights follow with it.',
    noun: 'Bush',
  },
}

/**
 * A NEW JOB OPENS THE WAY THIS SHOP WORKS.
 *
 * createDocument is pure and knows nothing about the browser, which is right.
 * What a fresh document inherits is how this shop works - its deposit policy,
 * its seasonal ladder, when it expects the balance and its own terms - so it
 * is applied here, on the way out of the factory, rather than baked into the
 * document type where a test would have to mock localStorage to read it.
 *
 * THIS IS THE ONLY PLACE THE DEFAULTS ARE READ, and that is the safeguard
 * rather than an accident of layout. A quote already written is a thing
 * somebody was shown and may have signed, so changing the shop's terms in
 * November must not rewrite what a homeowner agreed to in September. See
 * lib/defaults.ts and the test that walks a sold quote through applyTo and
 * asserts it comes out untouched.
 */
const freshDocument = (id: string): LightsOsDocument => {
  const doc = createDocument(id)
  const pref = loadDepositPref()
  const paper = applyTo(doc.paper ?? defaultPaper(), loadDefaults())
  return {
    ...doc,
    paper: { ...paper, deposit: { ...paper.deposit, mode: pref.mode, value: pref.value } },
  }
}

const cloneDoc = (d: LightsOsDocument): LightsOsDocument =>
  typeof structuredClone === 'function' ? structuredClone(d) : JSON.parse(JSON.stringify(d))

export interface StrandDefaults {
  product: ProductId
  palette: string[]
  patternRepeat: number
  bulbScale: number
  spacingScale: number
  brightness: number
  sag: number
  depthStart: number
  depthEnd: number
  wrapWidth: number
  wrapTurns: number
  wrapCap: boolean
  capPalette: string[]
  capLength: number
  capFronds: number
  capDroop: number
}

/**
 * Whatever you were last using is what the next run uses. That includes across
 * a reload and across photos: a contractor who works in warm white should not
 * have to say so again every morning.
 */
const DEFAULTS_KEY = 'lightsos.defaults.v1'

interface StickyDefaults {
  defaults: StrandDefaults
  /** Product is remembered per category, because a palm is never a C9. */
  productByCategory: Partial<Record<LightCategory, ProductId>>
}

const loadSticky = (base: StrandDefaults): StickyDefaults => {
  const fallback = { defaults: base, productByCategory: {} }
  try {
    const raw = localStorage.getItem(DEFAULTS_KEY)
    if (!raw) return fallback
    const saved = JSON.parse(raw) as Partial<StickyDefaults>
    return {
      // Merge over the built ins, so a setting added later is never undefined.
      defaults: { ...base, ...(saved.defaults ?? {}) },
      productByCategory: saved.productByCategory ?? {},
    }
  } catch {
    return fallback
  }
}

const saveSticky = (s: StickyDefaults) => {
  try {
    localStorage.setItem(DEFAULTS_KEY, JSON.stringify(s))
  } catch {
    /* private mode: the preference just does not survive the session */
  }
}

/** Read from outside, so the sync can carry these between a phone and a laptop. */
export const readSticky = (): StickyDefaults => loadSticky(initialDefaults)
export const writeSticky = (s: StickyDefaults) => saveSticky(s)

const initialDefaults: StrandDefaults = {
  product: 'C9',
  palette: [BULB_COLORS.warmWhite],
  patternRepeat: 1,
  bulbScale: 1,
  spacingScale: 1,
  brightness: 1,
  sag: 0,
  depthStart: 1,
  depthEnd: 0.82,
  wrapWidth: 0.028,
  wrapTurns: 14,
  wrapCap: false,
  capPalette: [BULB_COLORS.red],
  // The cap's height, as a fraction of the trunk. A real one is a foot or two
  // of a twenty foot trunk, and the reference install is about a sixth.
  capLength: 0.16,
  capFronds: 9,
  capDroop: 0.55,
}

const kindForTool = (tool: Tool): StrandKind => {
  switch (tool) {
    case 'freehand':
      return 'freeform'
    case 'wrap':
      return 'wrap'
    case 'swag':
      return 'swag'
    case 'cluster':
      return 'cluster'
    default:
      // 'roofline', 'auto' and 'shortEdge' all produce a roofline run. Auto
      // places the points for you; shortEdge leaves them exactly where the two
      // clicks landed.
      return 'roofline'
  }
}

const nameForCategory = (cat: LightCategory, n: number, palmMode?: 'trunk' | 'fronds') =>
  `${nounFor(cat, palmMode)} ${n}`

/** A frond is not a palm, and a list of runs has to say which one it is. */
export const nounFor = (cat: LightCategory, palmMode?: 'trunk' | 'fronds') =>
  cat === 'palms' && palmMode === 'fronds' ? 'Frond' : CATEGORY_SETUP[cat].noun

/**
 * WHAT KIND OF RUN A STROKE BECOMES.
 *
 * The tool says how it is drawn and the category says what it is. A frond and
 * a bush are drawn with the identical gesture - a hand following a shape in
 * the photograph - and they are two entirely different things once rendered: a
 * bush scatters its bulbs into an area, a frond is a line.
 *
 * Every caller that needs to know what is being drawn asks HERE. Doing it any
 * other way is what broke the look controls: they worked the kind out from
 * CATEGORY_SETUP, which says palms draw a wrap, so with Fronds armed the
 * spacing and bulb size landed on the trunks and never on the fronds. Reported
 * as: these do not work.
 */
const kindForDrawing = (
  st: { category: LightCategory; palmMode: 'trunk' | 'fronds' },
  tool: Tool,
): StrandKind =>
  tool === 'freehand' && st.category === 'palms' && st.palmMode === 'fronds'
    ? 'frond'
    : kindForTool(tool)

/**
 * WHETHER SNAPPING HAS ANYTHING TO SNAP TO.
 *
 * The straightener is handed the edge map for a roofline and a swag and for
 * nothing else, and the reason is written at that call: a wrapped trunk is
 * two taps describing the CENTER of the trunk, whose strongest nearby edges
 * are its sides, so snapping shoves the wrap off the tree, and a bush has no
 * straight line in it at all.
 *
 * The panel offers the switch only where that is true. Exported rather than
 * restated as a list of category names, because a list in a second file is a
 * list that goes stale the first time a category is added.
 */
export const snapsToEdges = (
  st: { category: LightCategory; palmMode: 'trunk' | 'fronds' },
  tool: Tool,
): boolean => {
  const kind = kindForDrawing(st, tool)
  return kind === 'roofline' || kind === 'swag'
}

/**
 * WHAT A CATEGORY DRAWS RIGHT NOW, IN ONE PLACE.
 *
 * Four call sites used to work this out for themselves - picking a category,
 * toggling the AI, switching roofline mode, and the sweep gesture asking what
 * it would have been - and every new mode meant remembering all four. The one
 * that got missed would leave the wrong tool armed, which reads as a click
 * doing nothing, or worse, drawing the wrong thing.
 */
const toolFor = (
  st: { rooflineMode: 'follow' | 'short'; palmMode: 'trunk' | 'fronds'; aiAssist: boolean },
  cat: LightCategory,
): Tool => {
  if (cat === 'roofline') {
    if (st.rooflineMode === 'short') return 'shortEdge'
    return st.aiAssist ? 'auto' : 'roofline'
  }
  // A frond is traced along a leaf that is already in the photograph, so it is
  // a freehand line, not a spiral around an axis.
  if (cat === 'palms' && st.palmMode === 'fronds') return 'freehand'
  return CATEGORY_SETUP[cat].tool
}

interface EditorState {
  doc: LightsOsDocument
  tool: Tool
  selectedId: string | null

  /** Points of the strand currently being drawn. */
  draft: Point[] | null
  /** Live cursor position, so the rubber band can preview the next segment. */
  hover: Point | null

  snapEnabled: boolean
  autoTrace: boolean
  edgeMap: EdgeMap | null

  calibDraft: { a: Point | null; b: Point | null }

  category: LightCategory
  setCategory: (c: LightCategory) => void

  /**
   * AI Assist is a mode across every category, not a setting buried in one of
   * them. Only the roofline has a real implementation today; the others say so
   * rather than pretending. The object model is untouched: AI produces exactly
   * the same runs the manual tools do.
   */
  aiAssist: boolean
  setAiAssist: (v: boolean) => void
  /** Which categories AI can actually do today. */
  aiReady: (c: LightCategory) => boolean

  /**
   * WHICH OF THE TWO ROOF TOOLS IS ARMED, REMEMBERED.
   *
   * Finishing a run drops the tool to select, so the tool alone cannot say
   * which way the person chose to draw. Without this the control snaps back
   * to "follow the edge" as soon as a run is finished, and the second short
   * edge on the same house quietly comes out grown.
   */
  rooflineMode: 'follow' | 'short'
  /**
   * A PALM IS TWO JOBS, AND THEY ARE DRAWN DIFFERENTLY.
   *
   * The trunk is two taps and a spiral of wrap. The crown is not: a palm is
   * lit by running a strand out along fronds that are ALREADY THERE in the
   * photograph, one at a time, following the curve of each leaf. There is no
   * geometry that turns a trunk into a crown, which is exactly why the old
   * checkbox that drew an even starburst of spokes above the trunk was the one
   * thing in this app inventing light where the photograph had none.
   *
   * So it is a mode on the same category rather than a category of its own:
   * the customer is buying "the palms", the quote has one palm line, and the
   * person drawing simply switches what their next stroke means. Same shape as
   * the roofline's follow and short modes, for the same reason.
   */
  palmMode: 'trunk' | 'fronds'
  setPalmMode: (m: 'trunk' | 'fronds') => void
  setRooflineMode: (m: 'follow' | 'short') => void

  /**
   * "GENERATE AI SUGGESTIONS", AS THE PANEL SEES IT.
   *
   * One in flight at a time, for one reason: every press is a paid call on a
   * photograph. The phase is what the button says; the message is what the
   * person is told when it goes wrong, and it always ends by saying the design
   * has not changed - because it has not.
   */
  suggest: {
    phase: SuggestPhase
    message: string | null
    /** How many runs the last successful press added. */
    added: number
    /**
     * The request id of the most recent successful generation, which is the
     * tag its proposals carry. Present means a regeneration would REPLACE
     * rather than append, and the button says so.
     */
    generation: string | null
    /** How many of that generation's suggestions are still unincluded. */
    replaceable: number
    /** The confirmation step is armed. Nothing is spent until it is taken. */
    confirming: boolean
  }
  /** The shared trial code, kept for this browser tab and nowhere else. */
  accessCode: string
  setAccessCode: (code: string) => void
  /**
   * The seam is for tests only: a mocked fetch and a virtual clock, so the
   * whole of this runs with no network and no waiting. Nothing in the app
   * passes it, and there is no default that would reach a network on its own.
   */
  generateSuggestions: (seam?: Partial<SuggestOptions>) => Promise<void>
  cancelSuggestions: () => void
  dismissSuggestMessage: () => void
  /** Arm or disarm the "this uses another AI generation" confirmation. */
  setSuggestConfirming: (v: boolean) => void

  /**
   * Everything past the essentials stays hidden until asked for. The person
   * using this installs lights for a living; a wall of sliders is how you lose
   * them in the first minute.
   */
  advanced: boolean
  setAdvanced: (v: boolean) => void

  /** Clean fullscreen preview for showing a customer. */
  present: boolean
  setPresent: (v: boolean) => void

  saveState: 'idle' | 'saving' | 'saved' | 'error'
  savedAt: number
  setSaveState: (s: 'idle' | 'saving' | 'saved' | 'error', at?: number) => void

  defaults: StrandDefaults
  /** Last product used in each category, so switching back restores it. */
  productByCategory: Partial<Record<LightCategory, ProductId>>

  past: LightsOsDocument[]
  future: LightsOsDocument[]

  setTool: (t: Tool) => void
  setPhoto: (dataUrl: string, width: number, height: number) => void
  setEdgeMap: (m: EdgeMap | null) => void
  setSnap: (v: boolean) => void
  setAutoTrace: (v: boolean) => void
  /** Timestamp of the last failed auto trace, so the UI can explain itself. */
  traceMiss: number
  setTraceMiss: (t: number) => void

  startDraft: (p: Point) => void
  /**
   * WAS THIS DRAFT SWIPED, OR TAPPED OUT CORNER BY CORNER?
   *
   * The straightener slides and tilts each piece onto the strongest edge near
   * it. That is right for a swipe, which is a rough "that shape, there", and
   * wrong for taps: somebody clicking the corners of a garage door is pointing
   * AT those corners, and moving each leg independently walks them off the
   * jambs and leaves the corners meeting somewhere nobody chose.
   *
   * Set by the sweep path. Cleared by startDraft, so a tapped run can never
   * inherit it from the run before.
   */
  draftSwiped: boolean
  markDraftSwiped: () => void
  addDraftPoints: (pts: Point[]) => void
  setHover: (p: Point | null) => void
  commitDraft: () => void
  cancelDraft: () => void
  popDraftPoint: () => void

  selectLayer: (id: string | null) => void
  updateStrand: (id: string, patch: Partial<Strand>) => void
  updateDecoration: (id: string, patch: Partial<Decoration>) => void
  addDecoration: (kind: Decoration['kind'], at: Point) => void
  /** Which product photo new decorations of each kind use. */
  decorStyle: Partial<Record<Decoration['kind'], string>>
  setDecorStyle: (kind: Decoration['kind'], id: string | undefined) => void
  deleteLayer: (id: string) => void
  duplicateLayer: (id: string) => void
  reorderLayer: (id: string, dir: -1 | 1) => void

  setScene: (patch: Partial<SceneSettings>) => void
  setBranding: (patch: Partial<Branding>) => void
  setWastePct: (v: number) => void
  /** Material settings: how the roof spool is spaced, how long a strand is. */
  setTakeoff: (patch: Partial<LightsOsDocument['takeoff']>) => void

  /* Pricing. The quote is derived from the document, so nothing here stores a
   * total; these only steer how the derivation runs. */
  setTier: (t: SeasonTier) => void
  setRate: (k: RateKey, v: number) => void
  setLightsPerStrand: (v: number) => void
  /** Patch one quote line. Passing undefined for a field returns it to derived. */
  setLineOverride: (key: string, patch: Partial<LineOverride>) => void
  clearLineOverride: (key: string) => void
  addExtra: () => void
  updateExtra: (id: string, patch: Partial<CustomLine>) => void
  removeExtra: (id: string) => void
  resetRates: () => void
  setDefaults: (patch: Partial<StrandDefaults>) => void
  /**
   * THE LOOK OF A CATEGORY IS A DECISION ABOUT THE HOUSE.
   *
   * Changing the color with nothing selected used to set the defaults only, so
   * the picture did not move and it read as broken - reported as: those
   * features are not working. It was working; it was just invisible, which for
   * a mockup tool is the same thing.
   *
   * A contractor does not think "this run is red". They think "the roof is
   * red". So with nothing selected, a change is the house's: it lands on every
   * run of that category AND on the next one drawn. Selecting a single run
   * still overrides it, which is the exception and is asked for explicitly by
   * selecting the run.
   */
  setCategoryLook: (patch: Partial<StrandDefaults>) => void

  /**
   * THE QUOTE, EDITED.
   *
   * Straight onto the document and straight into history, because a price is
   * a thing somebody typed and undo has to reach it like anything else. No
   * derivation anywhere: see lib/quote.ts.
   *
   * An ordered list rather than one line per category, because a job can want
   * two roofline lines - a front elevation at one rate and a back at another -
   * and the order they were typed in is the order they are read.
   */
  setQuoteLine: (id: string, patch: Partial<QuoteLine>) => void

  /**
   * The company sending the quote. Typed once, kept in this browser, and put
   * on the header of every quote from then on.
   */
  company: Company
  setCompany: (patch: Partial<Company>) => void
  /** The header, the seasonal ladder and the deposit. See lib/paper.ts. */
  setPaper: (patch: Partial<Paper>) => void
  /** Write down money that actually arrived. Never charges anybody. */
  recordPayment: (payment: Omit<Payment, 'id' | 'note'> & { note?: string }) => void
  /** Take a mistyped entry back out. Undo restores it. */
  removePayment: (id: string) => void
  setDeposit: (patch: Partial<Deposit>) => void
  /** A label makes it one of the EXTRA_LINES; without one it is the category. */
  addQuoteLine: (category: LightCategory, label?: string) => void
  removeQuoteLine: (id: string) => void

  setCalibPoint: (p: Point) => void
  applyCalibration: (feet: number, photoWidth: number, photoHeight: number) => void
  clearCalibration: () => void

  setDocName: (name: string) => void
  /**
   * A PERSON SAYS IT WENT OUT. NOTHING ELSE DOES.
   *
   * This used to fire when the document was printed or copied, on the theory
   * that those are the only ways a quote leaves. They are not: a print dialog
   * gets cancelled, a clipboard never gets pasted, a PDF gets saved to look at
   * again. Each of those told the job list that a customer was sitting on a
   * quote which had never reached them, and a pipeline counting quotes nobody
   * received is worse than one counting nothing at all.
   *
   * It never moves a job backward, or past an answer that has already come in.
   */
  markSent: () => void
  setCustomer: (patch: { customer?: string; address?: string }) => void
  loadDocument: (doc: LightsOsDocument) => void
  newDocument: () => void
  /** The drawing tool this category uses, for re-arming after a run finishes. */
  drawToolForCategory: () => Tool
  /** Wipe every run off this house, keeping the photo, the scale and the name. */
  clearRuns: () => void
  /** Detach a photo that cannot be displayed, keeping the design that was on it. */
  dropPhoto: () => void

  /* ------------------------------------------------ the confirmation layer */

  /**
   * Roof geometry nobody has agreed to yet. Rendered, selectable, editable,
   * and worth exactly nothing until it is confirmed.
   */
  selectedProposalId: string | null
  selectProposal: (id: string | null) => void

  /** Put fixture or detector output on the document. Never priced by doing so. */
  addProposals: (list: RoofProposal[]) => void
  /** A person says yes. Creates exactly one priced Strand, or explains why not. */
  confirmRun: (id: string) => { ok: boolean; blockers: Blocker[] }
  /** Move a run. Withdraws its confirmation and deletes its Strand, same frame. */
  editRunPoints: (id: string, points: Point[]) => void
  /**
   * The same edit, without a history entry, for the middle of a drag.
   *
   * beginRunEdit takes the one snapshot at pointerdown; every frame after it
   * goes through here, so an undo steps back over the whole drag rather than
   * one pixel of it. The reconcile still runs on every frame, so the priced
   * Strand disappears the moment the run moves, not when the finger lifts.
   */
  beginRunEdit: () => void
  editRunPointsLive: (id: string, points: Point[]) => void
  setSideReturn: (id: string, which: 'start' | 'end', decision: SideReturnDecision) => void
  setOcclusion: (id: string, spanIndex: number, decision: OcclusionDecision) => void
  removeRun: (id: string) => void

  /* ------------------------------------------------- places on the house */

  /**
   * A SECTION IS A PLACE, A RUN IS THE LIGHTS ON IT.
   *
   * Every one of these takes a section id, never a label and never an index,
   * because the id is the thing that survives a rename and a reorder. The
   * store is the only writer: geometry sync happens on the way in (see set
   * below), so no screen can leave a section pointing at a deleted run or
   * carrying a verification for a shape that has moved.
   */
  addSection: (patch?: Partial<RoofSection>) => string
  renameSection: (id: string, label: string) => void
  setSectionKind: (id: string, kind: SectionKind) => void
  /** Recoverable by design. Excluded is a decision, not a delete. */
  setSectionExcluded: (id: string, excluded: boolean, reason?: string) => void
  /** Removes the record. The runs it held stay on the drawing, unattached. */
  removeSection: (id: string) => void
  attachRunToSection: (sectionId: string, runId: string) => void
  detachRunFromSection: (runId: string) => void
  /** A guess with a number on it. Cannot reach a verified state, ever. */
  estimateSection: (
    id: string,
    input: { feet: number; source: Exclude<MeasureSource, 'field'>; plusMinus?: number; reason?: string },
  ) => void
  /** Somebody stood at the house. The only path to verified. */
  verifySection: (
    id: string,
    input: { feet: number; by: string; evidence?: string; reason?: string },
  ) => void

  /**
   * FREEZE WHAT SOMEBODY AGREED TO.
   *
   * Refuses, with reasons, rather than approving a scope built on a guess.
   * The blockers come back so a screen can say what to go and do; nothing is
   * written to the document unless the list is empty. Approving again appends
   * a second scope rather than replacing the first, so the history of what was
   * agreed and when survives. See lib/approval.ts.
   */
  approveSections: (input: { by: string; note?: string }) => {
    ok: boolean
    blockers: string[]
  }

  undo: () => void
  redo: () => void
}

export const useEditor = create<EditorState>((rawSet, get) => {
  const sticky = loadSticky(initialDefaults)

  /**
   * READ-ONLY WHEN THE PASS HAS LAPSED, IN ONE PLACE.
   *
   * A season of quotes is why a contractor comes back next October, so nothing
   * is deleted or hidden when a pass ends: he signs in, reads every customer
   * and every job, and opens anything he ever wrote. He just cannot make
   * anything new.
   *
   * There are over a hundred places in this file that change the document, and
   * a rule enforced at each of them is a rule missing from the one somebody
   * adds next month. So it is enforced HERE, on the way into the store, by
   * dropping any change to the document. Everything that is not the document
   * still goes through - selection, the open panel, the pan and zoom - because
   * none of that is work and freezing it would just read as broken.
   *
   * Loading a job is not editing, and lib/editable.ts carries the one named
   * escape for that.
   */
  /**
   * A SECTION CANNOT BE LEFT DESCRIBING A SHAPE THAT MOVED.
   *
   * There are over a hundred places in this file that change the document, and
   * the ones that redraw or delete a run were written years before sections
   * existed. Asking each of them to remember is asking for the one that
   * forgets, and the thing it would forget is a verified measurement quietly
   * being reused for a roof that is no longer that shape.
   *
   * So it happens HERE, on the way in, for the same reason the read-only rule
   * below does. Pure, and returns the identical array when nothing moved, so a
   * document that has no sections pays nothing.
   */
  const withSections = (d: LightsOsDocument): LightsOsDocument => {
    const cur = sectionsOf(d)
    const next = syncSections(cur, d.layers)
    return next === cur && Array.isArray(d.sections) ? d : { ...d, sections: next }
  }

  const set: typeof rawSet = ((partial, replace) => {
    const cur = get()
    const raw = typeof partial === 'function' ? (partial as (s: EditorState) => object)(cur) : partial
    let next = raw
    if (raw && typeof raw === 'object' && 'doc' in raw) {
      const d = (raw as { doc: LightsOsDocument }).doc
      // cur.doc can be undefined during store construction.
      if (d && d !== cur?.doc) {
        const synced = withSections(d)
        if (synced !== d) next = { ...(raw as object), doc: synced }
      }
    }
    if (canEdit()) return rawSet(next as never, replace as never)
    if (next && typeof next === 'object' && 'doc' in next && (next as EditorState).doc !== cur.doc) {
      const { doc: _doc, past: _past, future: _future, ...rest } = next as Record<string, unknown>
      return rawSet(rest as never, replace as never)
    }
    return rawSet(next as never, replace as never)
  }) as typeof rawSet

  /** Snapshot before any change that should be undoable. */
  const commit = () => {
    const { doc, past } = get()
    set({ past: [...past.slice(-49), cloneDoc(doc)], future: [] })
  }

  /**
   * SNAPSHOT AND CHANGE, IN ONE set().
   *
   * commit() above takes the snapshot in its own set(), which is fine for a
   * color slider and is not fine here. Requirement 6 is that there is never a
   * frame, and never a saved state, in which an edited proposal and its stale
   * priced Strand both exist - so a confirmation, the invalidation it implies,
   * and the history entry all have to land together or the guarantee is only
   * true between renders.
   *
   * Every proposal action goes through this. The reconcile is inside, so no
   * caller can produce a document where a moved run still owns a Strand.
   */
  const withHistory = (fn: (doc: LightsOsDocument) => LightsOsDocument, extra?: Partial<EditorState>) => {
    set((s) => {
      const before = cloneDoc(s.doc)
      const next = reconcile(fn(s.doc)).doc
      if (next === s.doc) return (extra ?? {}) as Partial<EditorState>
      return {
        ...(extra ?? {}),
        doc: next,
        past: [...s.past.slice(-49), before],
        future: [],
      } as Partial<EditorState>
    })
  }

  /**
   * ONE SECTION CHANGES, THE REST ARE UNTOUCHED, AND IT IS UNDOABLE.
   *
   * The patch is given the whole section and returns either a partial or a
   * complete replacement, so the measurement functions in lib/sections.ts -
   * which are the only things allowed to move a measurement - can be handed
   * straight in.
   */
  const patchSection = (
    id: string,
    fn: (s: RoofSection) => Partial<RoofSection>,
  ) => {
    withHistory((doc) => {
      const list = sectionsOf(doc)
      if (!list.some((x) => x.id === id)) return doc
      return { ...doc, sections: list.map((x) => (x.id === id ? { ...x, ...fn(x) } : x)) }
    })
  }

  return {
    doc: freshDocument(uid()),
    /*
     * NOTHING IS ARMED UNTIL SOMETHING IS CHOSEN.
     *
     * This opened on the roofline tool, so the first press on a photograph -
     * before anybody had said what they were doing - started drawing a roof
     * run. And once a category had been used, reopening put you back in it:
     * reported as, if I was using bow last and I restart it keeps that
     * selected, I want it to default to nothing until I select something.
     *
     * Select is that nothing. The bar reads Add rather than a category name,
     * which is the same neutral state pressing the armed category gets you.
     */
    tool: 'select',
    selectedId: null,
    draft: null,
    draftSwiped: false,
    hover: null,
    snapEnabled: true,
    autoTrace: false,
    traceMiss: 0,
    edgeMap: null,
    calibDraft: { a: null, b: null },
    category: 'roofline',
    aiAssist: true,
    /*
     * ADVANCED IS A VIEW FOR THIS SESSION, NOT A MODE THE APP REMEMBERS.
     *
     * It used to persist in localStorage, and that cost the owner an
     * afternoon. He turned it on at some point, forgot, and every session
     * after that opened an editor with a rail down each side and no dock. He
     * recorded four and a half minutes of feedback about a redesign that had
     * not happened: where is night and day, where is the middle bar, why are
     * there so many settings, this is completely different from what we had.
     * All of it was one invisible flag he did not know he had set.
     *
     * A preference a person cannot see, did not knowingly choose, and cannot
     * connect to what changed is not a preference. It is a trap. Opening the
     * tools rail is now something you do to this session, and every reload
     * starts where everybody else starts.
     */
    advanced: false,
    present: false,
    decorStyle: { wreath: 'wreath-warm-white', garland: 'garland-warm-top', bow: 'bow-red' },
    saveState: 'idle',
    savedAt: 0,
    defaults: sticky.defaults,
    productByCategory: sticky.productByCategory,
    past: [],
    future: [],
    selectedProposalId: null,
    rooflineMode: 'follow',
    palmMode: 'trunk',
    company: loadCompany(),

    setTool: (tool) => set({ tool, draft: null, hover: null }),

    setCategory: (category) => {
      const setup = CATEGORY_SETUP[category]
      // Garland is roughly ten inches thick, a trunk is not. Carry the right
      // starting width into the category rather than inheriting the last one.
      const width =
        category === 'garland' ? 0.03 : category === 'trees' || category === 'columns' ? 0.028 : undefined
      set((st) => ({
        category,
        tool: toolFor(get(), category),
        draft: null,
        hover: null,
        selectedId: null,
        // Come back to Roofline and you get the product you were last using
        // there, not a reset. Color, brightness and the rest never change on a
        // category switch at all.
        defaults: {
          ...st.defaults,
          product: st.productByCategory[category] ?? setup.product,
          ...(width ? { wrapWidth: width } : {}),
        },
      }))
    },

    setAiAssist: (aiAssist) => {
      const st = get()
      set({
        aiAssist,
        tool: toolFor({ ...st, aiAssist }, st.category),
        draft: null,
        hover: null,
      })
    },

    aiReady: (c) => c === 'roofline',

    setRooflineMode: (rooflineMode) =>
      set((st) => ({
        rooflineMode,
        tool: toolFor({ ...st, rooflineMode }, st.category),
        draft: null,
        hover: null,
      })),

    setPalmMode: (palmMode) =>
      set((st) => ({
        palmMode,
        tool: toolFor({ ...st, palmMode }, st.category),
        draft: null,
        hover: null,
        // The trunk you were looking at is not the frond you are about to
        // draw, and leaving it selected puts the wrong controls on screen.
        selectedId: null,
      })),

    /*
     * What a press and drag on empty photo should start.
     *
     * Committing a run drops the tool to select so that clicking around does
     * not draw. The sweep gesture has to keep working from there or the fix
     * costs a tap on every run, so it asks here what it would have been.
     */
    drawToolForCategory: () => toolFor(get(), get().category),

    setAdvanced: (advanced) => {
      /*
       * Deliberately not written down. See the note on the initial value: this
       * is a view for the session. The old key is cleared on the way past so
       * anybody already carrying it stops being stranded by it.
       */
      try {
        localStorage.removeItem('lightsos.advanced')
      } catch {
        /* private mode, nothing to clear */
      }
      set({ advanced })
    },

    setPresent: (present) => set({ present }),

    setDecorStyle: (kind, id) =>
      set((st) => ({ decorStyle: { ...st.decorStyle, [kind]: id } })),

    setSaveState: (saveState, at) =>
      set({ saveState, savedAt: at ?? (saveState === 'saved' ? Date.now() : get().savedAt) }),

    setPhoto: (dataUrl, width, height) => {
      commit()
      set((s) => {
        const photo = { dataUrl, width, height }
        /*
         * A new photo on a job that already has work is a NEW job.
         *
         * It used to swap the image and keep everything: the last house's
         * wreath stayed on the new house, its calibration stayed with it, and
         * because the id never changed the whole thing saved over the job you
         * had just finished. Now that jobs persist, the old one stays safely
         * under its own name and this starts a clean sheet.
         *
         * Replacing the photo on an empty mockup is just replacing the photo,
         * which is what someone re-shooting a bad angle expects.
         *
         * And a job that has runs but NO photo is a job whose image went
         * missing - a failed write, an evicted database. Every run is stored in
         * normalized coordinates, so dropping the same photograph back in puts
         * the whole design exactly where it was. Starting a new job there would
         * throw away the very work the user is trying to get back.
         */
        if (s.doc.layers.length === 0 || !s.doc.photo) {
          return { doc: { ...s.doc, photo }, selectedId: null, draft: null }
        }
        const fresh = freshDocument(uid())
        return {
          doc: { ...fresh, photo, pricing: s.doc.pricing, branding: s.doc.branding },
          selectedId: null,
          draft: null,
          past: [],
          future: [],
        }
      })
    },

    setEdgeMap: (edgeMap) => set({ edgeMap }),
    setSnap: (snapEnabled) => set({ snapEnabled }),
    setAutoTrace: (autoTrace) => set({ autoTrace }),
    setTraceMiss: (traceMiss) => set({ traceMiss }),

    startDraft: (p) => set({ draft: [p], selectedId: null, draftSwiped: false }),

    markDraftSwiped: () => set({ draftSwiped: true }),

    addDraftPoints: (pts) =>
      set((s) => ({ draft: s.draft ? [...s.draft, ...pts] : [...pts] })),

    setHover: (hover) => set({ hover }),

    popDraftPoint: () =>
      set((s) => (s.draft && s.draft.length > 1 ? { draft: s.draft.slice(0, -1) } : { draft: s.draft })),

    commitDraft: () => {
      const { draft, doc, defaults, tool, edgeMap, snapEnabled, draftSwiped } = get()
      if (!draft || draft.length < 2) {
        set({ draft: null, hover: null })
        return
      }
      commit()
      /*
       * A FROND IS NOT A BUSH.
       *
       * Both are drawn with the same gesture - a hand following a shape in the
       * photograph - so they share the freehand tool and every bit of its
       * dragging, and they are two different things the moment they are
       * rendered. The category is what says which, and it says it once, here.
       */
      const drawnCat = get().category
      const kind = kindForDrawing(get(), tool)

      /*
       * Straighten what was drawn before it becomes a run.
       *
       * The hand is not the authority on where the roof is - the photo is. A
       * rough drag gets reduced to its corners and each piece slid onto the
       * real architectural line under it, so a line drawn in a driveway on a
       * tablet ends up exactly on the fascia.
       *
       * Rooflines and garland only. A wrapped trunk is two taps describing the
       * CENTER of the trunk, and its two strongest nearby edges are the sides,
       * so snapping would shove the wrap off the tree. A bush has no straight
       * line in it at all, so it gets the corner reduction and nothing else.
       */
      const points =
        /*
         * THE SHORT EDGE IS NOT STRAIGHTENED AND NOT GROWN.
         *
         * Two clicks, two points, in the order they were made. Everything the
         * straightener does is right for a long fascia and wrong here: the
         * recessed edge this tool exists for is never the strongest line near
         * itself, so sliding onto that line moves the run off the thing the
         * contractor was pointing at, and growing it out to the line's ends
         * makes a short run long.
         */
        tool === 'shortEdge'
          ? [draft[0], draft[draft.length - 1]]
          /*
           * A TRUNK IS A STRAIGHT LINE BETWEEN ITS TWO ENDS.
           *
           * A wrap kept every point of the stroke, so the spiral followed the
           * hand: go up a palm and the wrap leans left, then right, then left,
           * because that is what a finger does over eighteen inches of glass.
           * A trunk does not do that. Reported as: for the palm tree it should
           * be a straight line, when I go straight up and down it should not
           * sway to the left or to the right, kind of like a roofline from one
           * point to another.
           *
           * So the two ends are kept and everything between them is dropped.
           * Not straightened onto anything: a trunk is not an architectural
           * edge and there is nothing under it worth snapping to. Just the
           * line the two ends describe.
           *
           * A palm that really does lean through a bend is two runs, the same
           * way a gable is two roof runs - a rule this app already makes
           * everywhere else.
           */
          : kind === 'wrap'
          ? [draft[0], draft[draft.length - 1]]
          /*
           * A SHAPE TAPPED OUT CORNER BY CORNER IS KEPT AS TAPPED.
           *
           * Garland round a garage door is up one jamb, across the header,
           * down the other - three legs and two corners, every one of them a
           * place somebody aimed at. The straightener slides and tilts each
           * leg independently onto the strongest edge near it and then rebuilds
           * the corners by intersecting the moved legs, so the legs drift off
           * the jambs and the corners end up somewhere nobody chose.
           *
           * That is the right behavior for a SWIPE, which is a rough gesture
           * meaning "that shape, there". It is the wrong behavior for taps, in
           * exactly the way it was wrong for a two tap roofline, and for the
           * same reason.
           *
           * ONLY multi-corner shapes are exempted. A two tap run still goes
           * through the straightener, because there the movement is
           * perpendicular and the length is held - it locks onto the fascia
           * without either end travelling along it, which is worth having and
           * is not what anybody complained about.
           */
          : !draftSwiped && draft.length > 2
          ? draft
          : kind === 'roofline' || kind === 'swag'
          ? straightenRun(
              draft,
              snapEnabled ? edgeMap : null,
              // Rooflines grow to the ends of the edge; garland does not. A
              // garage header run stops where the header stops, and running it
              // on to the next strong line would walk it across the wall.
              kind === 'roofline',
            )
          : kind === 'freeform' || kind === 'frond'
            ? /*
               * No edge map either way. A bush has no straight line in it, and
               * a frond follows a leaf rather than any architecture, so both
               * get the shake taken out and the shape kept: straightenRun tells
               * a bow from a wobbly straight line and smooths the one while
               * straightening the other.
               */
              straightenRun(draft, null)
            : draft
      /*
       * TRIM ANYTHING THAT ENDED UP IN THE SKY.
       *
       * A roofline is allowed to grow along the edge it was drawn on, and at a
       * gable peak the tile silhouette against the sky is one of the strongest
       * edges in the photograph - so the run followed it straight off the end
       * of the roof and left a line of bulbs hanging in mid air above the
       * apex. It is in the exported hero, which is where it was spotted.
       *
       * Stopping the walk when it enters sky is the right fix and it is done
       * where the walking happens. This is the backstop, because the run can
       * also arrive here from the facet matcher, and no path should be able to
       * put a light where nothing can be installed.
       */
      const trimmed = (() => {
        const m = edgeMap
        if (!m || (kind !== 'roofline' && kind !== 'swag') || points.length < 2) return points
        /*
         * A DELIBERATE RUN IS THE PERSON POINTING AT SOMETHING.
         *
         * This backstop exists for runs the app GREW along an edge, which
         * could follow a tile silhouette off the end of a roof and hang bulbs
         * in the sky. It has no business shortening a line somebody drew on
         * purpose - and two taps are two decisions whichever roof tool placed
         * them, not only the short edge one. Reported as: it makes it longer
         * or shorter.
         */
        if (tool === 'shortEdge' || points.length === 2) return points
        const inSky = (p: Point) => {
          const x = Math.min(m.w - 1, Math.max(0, Math.round(p.x * m.w)))
          const y = Math.min(m.h - 1, Math.max(0, Math.round(p.y * m.h)))
          return m.sky[y * m.w + x] === 1
        }
        const a = points[0]
        const b = points[points.length - 1]
        if (points.length === 2 && (inSky(a) || inSky(b))) {
          // Walk the offending end back along the run until it is on the house.
          const pull = (from: Point, toward: Point) => {
            let lo = 0
            let hi = 1
            for (let i = 0; i < 14; i++) {
              const t = (lo + hi) / 2
              const q = { x: from.x + (toward.x - from.x) * t, y: from.y + (toward.y - from.y) * t }
              if (inSky(q)) lo = t
              else hi = t
            }
            return { x: from.x + (toward.x - from.x) * hi, y: from.y + (toward.y - from.y) * hi }
          }
          return [inSky(a) ? pull(a, b) : a, inSky(b) ? pull(b, a) : b]
        }
        return points.filter((p, i) => (i === 0 || i === points.length - 1 ? !inSky(p) : true))
      })()

      const cat = drawnCat
      const noun = nounFor(cat, get().palmMode)
      const count = doc.layers.filter((l) => l.name.startsWith(noun)).length + 1
      const strand: Strand = {
        id: uid(),
        type: 'strand',
        kind,
        name: nameForCategory(cat, count, get().palmMode),
        visible: true,
        points: trimmed.length >= 2 ? trimmed : points,
        product: defaults.product,
        palette: [...defaults.palette],
        // Garland is a photographed product tiled along the run, so the run
        // has to remember which one it is.
        styleId: kind === 'swag' ? get().decorStyle.garland : undefined,
        patternRepeat: defaults.patternRepeat,
        bulbScale: defaults.bulbScale,
        spacingScale: defaults.spacingScale,
        brightness: defaults.brightness,
        /*
         * A garland run starts STRAIGHT.
         *
         * The old default forced a 0.35 catenary on anything drawn as a swag,
         * from back when a swag meant a rope slung between two porch columns.
         * Garland is nearly always mounted flush - a garage header, a door
         * frame, a railing - so the droop was fighting the line the user had
         * just drawn: they clicked the header and the garland hung a foot
         * below it. Droop is a thing you ADD, on the run that wants it.
         */
        sag: kind === 'swag' ? 0 : defaults.sag,
        // Flush is the common case by a wide margin - garages, doors, windows -
        // so it is what you get without asking. The other two are one tap away.
        garlandShape: kind === 'swag' ? 'flush' : undefined,
        swagCount: kind === 'swag' ? 4 : undefined,
        depthStart: defaults.depthStart,
        depthEnd: defaults.depthEnd,
        /*
         * A BRANCH IS THINNER THAN THE TRUNK.
         *
         * On a shade tree the first run is the trunk and everything after it is
         * a limb you go up, each thinner than the last thing you drew. Making
         * every run the same width meant every branch arrived as thick as the
         * trunk and had to be dialled down by hand, which on a tree with five
         * limbs is five trips to the slider.
         *
         * Only in the trees category, and only after the trunk exists. A palm
         * has one trunk and no branches, and a column is a column.
         */
        wrapWidth:
          cat === 'trees' && doc.layers.some((l) => l.name.startsWith('Tree'))
            ? Math.max(0.008, defaults.wrapWidth * 0.55)
            : defaults.wrapWidth,
        wrapTurns: defaults.wrapTurns,
        // A palm is a trunk AND a crown, and the crown is most of what the
        // customer is paying for, so it arrives lit rather than hidden behind
        // a toggle. A shade tree has no crown to light.
        wrapCap: cat === 'palms' ? true : cat === 'trees' ? false : defaults.wrapCap,
        capPalette: [...defaults.capPalette],
        capLength: defaults.capLength,
        capFronds: defaults.capFronds,
        capDroop: defaults.capDroop,
      }

      /*
       * A ROOF RUN YOU DREW YOURSELF STILL GETS CONFIRMED.
       *
       * Requirement 5, and it is not bureaucracy. The line that goes on the
       * invoice is the line after straightening and sky trimming, not the line
       * your finger drew, and those are routinely a few feet apart at the ends
       * where the run grows along the edge it found. Confirming is the moment
       * you look at what the app decided and agree with it.
       *
       * Bushes, trunks and garland do not go through this. They are not roof
       * edges, nothing detects them, and there is no hidden footage to argue
       * about.
       */
      const asProposal = needsConfirmation(kind)
        ? {
            ...makeProposal({
              id: uid(),
              points: strand.points,
              origin: 'manual',
              createdAt: new Date().toISOString(),
            }),
            seq: nextSeq(doc),
          }
        : null

      /*
       * A LINE YOU DREW IS LIT THE MOMENT YOU DRAW IT.
       *
       * The confirmation step exists so that geometry NOBODY AGREED TO cannot
       * reach a quote. That is a rule about the model's guesses, not about
       * your hand. Drawing a line on a roof IS the agreement; asking you to
       * then press Include on the thing you just drew is asking you to agree
       * with yourself.
       *
       * So a run you draw is confirmed on the spot, becomes a priced Strand,
       * and renders as bulbs immediately. Reported as: I shouldn't have to
       * click anywhere else, I should see the lights as soon as I put the
       * line.
       *
       * The boundary is untouched where it matters. An AI proposal still
       * arrives dashed and unlit and still needs a tap, because that geometry
       * came from a model. And confirmProposal refuses on its own terms, so a
       * run it will not accept stays a proposal rather than being forced
       * through - the same blockers, just nobody made you press a button to
       * discover them.
       */
      const confirmed =
        asProposal &&
        confirmProposalDoc(
          countCorrection(
            { ...doc, proposals: [...doc.proposals, asProposal] },
            { runsDrawnManually: 1, runsProposed: 1 },
          ),
          asProposal.id,
          {
            now: new Date().toISOString(),
            strandId: uid(),
            template: { ...get().defaults },
            name: 'Roof run',
          },
        )

      set((s) => ({
        doc: confirmed && confirmed.ok && !confirmed.noop
          ? confirmed.doc
          : asProposal
            ? countCorrection(
                { ...s.doc, proposals: [...s.doc.proposals, asProposal] },
                { runsDrawnManually: 1, runsProposed: 1 },
              )
            : { ...s.doc, layers: [...s.doc.layers, strand] },
        selectedProposalId: null,
        draft: null,
        hover: null,
        /*
         * THE TOOL STAYS ARMED, SO ONE HOUSE IS ONE GESTURE REPEATED.
         *
         * It used to drop to select after every run, which fixed a real
         * complaint - every click anywhere was the start of something - and
         * created a worse one: six runs on a house meant pressing the category
         * six times. Reported three separate ways, ending with "I don't need
         * to click roof every time".
         *
         * The original problem is smaller than it looked. A stray tap starts a
         * DRAFT, not a run: nothing is created until a second point lands, and
         * tapping elsewhere or pressing Escape throws the draft away. So the
         * cost of staying armed is a cancelable half-gesture, and the cost of
         * disarming was a press per run all day.
         */
        selectedId: null,
      }))
    },

    cancelDraft: () => set({ draft: null, hover: null }),

    selectLayer: (selectedId) => set({ selectedId }),

    updateStrand: (id, patch) => {
      /*
       * A CONFIRMED RUN CANNOT BE RESHAPED IN PLACE.
       *
       * Dragging a handle on a priced roofline is a geometry edit, and
       * geometry edits belong to the proposal, not to the Strand it produced.
       * Routing it here rather than in the renderer means every caller gets
       * the invalidation - including one written next year by someone who has
       * never read this file.
       *
       * Color, brightness, name and product are not geometry, so they patch
       * the Strand directly and the run stays confirmed.
       */
      const owner = patch.points
        ? get().doc.proposals.find((q) => q.strandId === id)
        : undefined
      if (owner) {
        const points = patch.points as Point[]
        withHistory((doc) => {
          const out = editProposal(doc, owner.id, { points })
          return relight(doc, countCorrection(out.doc, { runsEdited: 1, circlesMoved: 1 }), owner.id)
        }, { selectedProposalId: owner.id })
        return
      }
      commit()
      set((s) => ({
        doc: {
          ...s.doc,
          layers: s.doc.layers.map((l) => (l.id === id && isStrand(l) ? { ...l, ...patch } : l)),
        },
      }))
    },

    updateDecoration: (id, patch) => {
      commit()
      set((s) => ({
        doc: {
          ...s.doc,
          layers: s.doc.layers.map((l) =>
            l.id === id && l.type === 'decoration' ? { ...l, ...patch } : l,
          ),
        },
      }))
    },

    addDecoration: (kind, at) => {
      commit()
      const count = get().doc.layers.filter((l) => l.type === 'decoration').length + 1
      const dec: Decoration = {
        id: uid(),
        type: 'decoration',
        kind,
        name: `${kind[0].toUpperCase()}${kind.slice(1)} ${count}`,
        visible: true,
        x: at.x,
        y: at.y,
        // A garland run frames an opening, so it starts far wider than a
        // wreath. Starting everything at 1 meant every garland arrived as a
        // stub you had to stretch before you could tell what it was.
        scale: kind === 'garland' ? 3.2 : kind === 'bow' ? 0.55 : 1,
        // Off by default. Added bulbs are radial blobs; a photographed C9 has
        // facets, a filament and a green wire behind it, and at any real zoom
        // the difference is obvious. A denser photograph beats them, so this
        // stays as a fallback for a product we do not have a dense shot of.
        extraBulbs: 0,
        rotation: 0,
        // A wreath is lit in whatever color you are working in, same as a run.
        palette: [...get().defaults.palette],
        styleId: get().decorStyle[kind] ?? defaultStyleFor(kind)?.id,
      }
      /*
       * Drop the tool after placing. It stayed armed, so the next click put
       * down a second wreath instead of selecting the first - which meant the
       * size controls could never be reached again once you clicked away.
       * Every design tool switches to select after a place; this one now does
       * too. Place another from the panel button.
       */
      set((s) => ({
        doc: { ...s.doc, layers: [...s.doc.layers, dec] },
        selectedId: dec.id,
        tool: 'select',
      }))
    },

    deleteLayer: (id) => {
      // Deleting a priced roof run is deleting the run, so its confirmation
      // goes with it rather than being left pointing at nothing.
      const owner = get().doc.proposals.find((q) => q.strandId === id)
      if (owner) {
        withHistory((doc) => removeProposalDoc(doc, owner.id), {
          selectedId: null,
          selectedProposalId: null,
        })
        return
      }
      commit()
      set((s) => ({
        doc: { ...s.doc, layers: s.doc.layers.filter((l) => l.id !== id) },
        selectedId: s.selectedId === id ? null : s.selectedId,
      }))
    },

    duplicateLayer: (id) => {
      const st = get()
      const layer = st.doc.layers.find((l) => l.id === id)
      if (!layer) return

      /*
       * A COPY OF A ROOF RUN IS A NEW PROPOSAL, NOT A SECOND PRICED RUN.
       *
       * Duplicating is how you start the run on the next elevation: it lands
       * on top of the original and gets dragged into place, which is a piece
       * of geometry nobody has looked at yet. Copying the Strand instead would
       * put a second roofline straight onto the invoice at the moment it is
       * least deserved.
       */
      const owner = st.doc.proposals.find((q) => q.strandId === id)
      if (owner) {
        const copy = makeProposal({
          id: uid(),
          points: owner.points,
          origin: 'manual',
          createdAt: new Date().toISOString(),
          kind: owner.kind,
        })
        withHistory(
          (doc) =>
            countCorrection({ ...doc, proposals: [...doc.proposals, copy] }, {
              runsProposed: 1,
              runsDrawnManually: 1,
            }),
          { selectedProposalId: copy.id, selectedId: null },
        )
        return
      }

      commit()
      const copy: Layer = { ...cloneLayer(layer), id: uid(), name: `${layer.name} copy` }
      set((s) => ({ doc: { ...s.doc, layers: [...s.doc.layers, copy] }, selectedId: copy.id }))
    },

    reorderLayer: (id, dir) => {
      const { doc } = get()
      const i = doc.layers.findIndex((l) => l.id === id)
      const j = i + dir
      if (i < 0 || j < 0 || j >= doc.layers.length) return
      commit()
      const layers = [...doc.layers]
      ;[layers[i], layers[j]] = [layers[j], layers[i]]
      set((s) => ({ doc: { ...s.doc, layers } }))
    },

    setScene: (patch) =>
      set((s) => ({ doc: { ...s.doc, scene: { ...s.doc.scene, ...patch } } })),

    setBranding: (patch) =>
      set((s) => ({ doc: { ...s.doc, branding: { ...s.doc.branding, ...patch } } })),

    setTakeoff: (patch) =>
      set((s) => ({ doc: { ...s.doc, takeoff: { ...s.doc.takeoff, ...patch } } })),

    setWastePct: (wastePct) =>
      set((s) => ({ doc: { ...s.doc, takeoff: { ...s.doc.takeoff, wastePct } } })),

    setTier: (tier) => set((s) => ({ doc: { ...s.doc, pricing: { ...s.doc.pricing, tier } } })),

    setRate: (k, v) =>
      set((s) => ({
        doc: {
          ...s.doc,
          pricing: { ...s.doc.pricing, rates: { ...s.doc.pricing.rates, [k]: v } },
        },
      })),

    setLightsPerStrand: (lightsPerStrand) =>
      set((s) => ({ doc: { ...s.doc, pricing: { ...s.doc.pricing, lightsPerStrand } } })),

    setLineOverride: (key, patch) =>
      set((s) => {
        const next = { ...s.doc.pricing.overrides[key], ...patch }
        // Undefined means "go back to what the drawing says", so strip it out
        // rather than storing a hole that looks like an edit.
        for (const k of Object.keys(next) as (keyof LineOverride)[]) {
          if (next[k] === undefined) delete next[k]
        }
        return {
          doc: {
            ...s.doc,
            pricing: {
              ...s.doc.pricing,
              overrides: { ...s.doc.pricing.overrides, [key]: next },
            },
          },
        }
      }),

    clearLineOverride: (key) =>
      set((s) => {
        const overrides = { ...s.doc.pricing.overrides }
        delete overrides[key]
        return { doc: { ...s.doc, pricing: { ...s.doc.pricing, overrides } } }
      }),

    addExtra: () => {
      commit()
      set((s) => ({
        doc: {
          ...s.doc,
          pricing: {
            ...s.doc.pricing,
            extras: [...s.doc.pricing.extras, { id: uid(), label: '', qty: 1, rate: 0 }],
          },
        },
      }))
    },

    updateExtra: (id, patch) =>
      set((s) => ({
        doc: {
          ...s.doc,
          pricing: {
            ...s.doc.pricing,
            extras: s.doc.pricing.extras.map((x) => (x.id === id ? { ...x, ...patch } : x)),
          },
        },
      })),

    removeExtra: (id) => {
      commit()
      set((s) => ({
        doc: {
          ...s.doc,
          pricing: {
            ...s.doc.pricing,
            extras: s.doc.pricing.extras.filter((x) => x.id !== id),
          },
        },
      }))
    },

    resetRates: () => {
      commit()
      set((s) => ({ doc: { ...s.doc, pricing: { ...s.doc.pricing, rates: defaultRates() } } }))
    },

    setPaper: (patch) =>
      withHistory((doc) => ({ ...doc, paper: { ...(doc.paper ?? defaultPaper()), ...patch } })),

    setDeposit: (patch) =>
      withHistory((doc) => {
        const paper = doc.paper ?? defaultPaper()
        const deposit = { ...paper.deposit, ...patch }
        /*
         * What a shop asks for up front is a policy, not a fact about this
         * house, so it is remembered the way the letterhead is. Only the ask
         * is remembered: whether anybody has PAID is a fact about one job and
         * has no business travelling to the next one.
         */
        if (patch.mode !== undefined || patch.value !== undefined) {
          saveDepositPref({ mode: deposit.mode, value: deposit.value })
        }
        return { ...doc, paper: { ...paper, deposit } }
      }),

    /*
     * MONEY THAT ARRIVED. Recording is bookkeeping, never collection.
     *
     * Nothing in this app charges anybody. A homeowner hands over a check on
     * the kitchen table or sends a Zelle from the driveway, and this is where
     * the contractor writes it down. It is a list because part payments are
     * ordinary and a single paid flag cannot describe one.
     */
    recordPayment: (payment) =>
      withHistory((doc) => {
        const paper = doc.paper ?? defaultPaper()
        const list = Array.isArray(paper.payments) ? paper.payments : []
        const entry: Payment = { id: uid(), note: '', ...payment }
        return {
          ...doc,
          paper: {
            ...paper,
            // Oldest first, so a history reads the way a ledger does.
            payments: [...list, entry].sort((a, b) => (a.date < b.date ? -1 : 1)),
          },
        }
      }),

    /*
     * A CORRECTION, NOT A DELETION OF THE TRUTH. Somebody typed 500 when they
     * meant 50, and the fix is to take the wrong line out rather than to leave
     * a customer's balance wrong. Undo puts it back.
     */
    removePayment: (id) =>
      withHistory((doc) => {
        const paper = doc.paper ?? defaultPaper()
        return {
          ...doc,
          paper: { ...paper, payments: (paper.payments ?? []).filter((q) => q.id !== id) },
        }
      }),

    setCompany: (patch) =>
      set((st) => {
        const company = { ...st.company, ...patch }
        saveCompany(company)
        return { company }
      }),

    setQuoteLine: (id, patch) => {
      withHistory((doc) => ({
        ...doc,
        quote: (doc.quote ?? []).map((l) => (l.id === id ? { ...l, ...patch } : l)),
      }))
    },

    addQuoteLine: (category, label) => {
      /*
       * The first line of a job is what tears a number off the pad. Not
       * opening the screen, which somebody does to look at a job they are not
       * quoting, and not creating the document, which happens every time a
       * photograph is uploaded. Either of those leaves gaps in the book.
       */
      const st = get()
      if (!(st.doc.paper?.number ?? '').trim()) {
        const { number, company } = nextDocNumber(st.company)
        saveCompany(company)
        set({ company })
        st.setPaper({ number })
      }
      withHistory((doc) => ({
        ...doc,
        quote: [
          ...(doc.quote ?? []),
          { id: uid(), category, ...emptyLine(), ...(label ? { label } : {}) },
        ],
      }))
    },

    removeQuoteLine: (id) => {
      withHistory((doc) => ({ ...doc, quote: (doc.quote ?? []).filter((l) => l.id !== id) }))
    },

    setCategoryLook: (patch) => {
      /*
       * The same mapping the drawing path uses, so what a change lands on is
       * exactly what the next run would be.
       *
       * Except for the categories that place a DECORATION rather than draw a
       * run. kindForTool falls through to 'roofline' for those, so without
       * this a color change while Wreaths was armed would repaint every
       * roofline on the house. Their look is a picture, not a palette, and
       * they have nothing here to change.
       */
      /*
       * toolFor, not CATEGORY_SETUP. The setup table says what a category
       * draws by default; toolFor says what it is drawing RIGHT NOW, which is
       * the only thing that matches what the next run will actually be. With
       * Fronds armed the two disagree, and taking the wrong one put every
       * spacing and bulb size change onto the trunks.
       */
      const st0 = get()
      const tool = toolFor(st0, st0.category)
      const draws = tool !== 'wreath' && tool !== 'accessory'
      const kind = kindForDrawing(st0, tool)
      set((st) => {
        const defaults = { ...st.defaults, ...patch }
        /*
         * AND IT IS REMEMBERED, like every other default.
         *
         * setDefaults has always written these through to storage; this path
         * was added later and did not, so a contractor who set his spacing
         * here got it back on the next run and lost it on the next JOB. Asked
         * as: shouldn't it save as default, or would that make it complicated.
         * It does not: the machinery was already there and this was the one
         * door into it that did not use it.
         */
        saveSticky({ defaults, productByCategory: st.productByCategory })
        return {
          defaults,
          doc: {
            ...st.doc,
            layers: draws
              ? st.doc.layers.map((l) =>
                  isStrand(l) && l.kind === kind ? { ...l, ...patch } : l,
                )
              : st.doc.layers,
          },
        }
      })
    },

    setDefaults: (patch) =>
      set((s) => {
        const defaults = { ...s.defaults, ...patch }
        const productByCategory = patch.product
          ? { ...s.productByCategory, [s.category]: patch.product }
          : s.productByCategory
        saveSticky({ defaults, productByCategory })
        return { defaults, productByCategory }
      }),

    setCalibPoint: (p) =>
      set((s) => {
        const { a, b } = s.calibDraft
        if (!a || (a && b)) return { calibDraft: { a: p, b: null } }
        return { calibDraft: { a, b: p } }
      }),

    applyCalibration: (feet, photoWidth, photoHeight) => {
      const { a, b } = get().calibDraft
      if (!a || !b || feet <= 0) return
      const dx = (b.x - a.x) * photoWidth
      const dy = (b.y - a.y) * photoHeight
      const px = Math.hypot(dx, dy)
      if (px < 2) return
      commit()
      set((s) => ({
        doc: {
          ...s.doc,
          calibration: { pixelsPerFoot: px / feet, refA: a, refB: b, knownFeet: feet },
        },
        /*
         * The measuring line goes away once it has been used.
         *
         * It was left on screen, so every mockup carried a bright green line
         * with two handles down the front door - in the editor, in Present and
         * in the exported JPEG. The measurement is kept on the document, which
         * is where it belongs; the draft that produced it is finished.
         */
        calibDraft: { a: null, b: null },
        /*
         * AND THE TOOL GOES BACK TO SELECT.
         *
         * The two panels disagreed: the simple one dropped to Select after
         * Apply, the advanced one armed the roofline tool - so on the advanced
         * side the next tap, which a person means as "now let me look at that
         * run", started drawing a new one across the house. Measuring is
         * setup; what follows it is looking, not drawing.
         */
        tool: 'select' as Tool,
      }))
    },

    clearCalibration: () => {
      commit()
      set((s) => ({
        doc: {
          ...s.doc,
          calibration: { pixelsPerFoot: null, refA: null, refB: null, knownFeet: null },
        },
        calibDraft: { a: null, b: null },
      }))
    },

    setDocName: (name) => set((s) => ({ doc: { ...s.doc, name } })),

    markSent: () =>
      withHistory((doc) => (doc.status === 'draft' ? { ...doc, status: 'quoted' } : doc)),

    setCustomer: (patch) => set((s) => ({ doc: { ...s.doc, ...patch } })),

    // Opening a saved job replaces the document, and it has to keep working
    // when a pass has lapsed or read-only means nothing. See lib/editable.ts.
    loadDocument: (doc) =>
      loading(() =>
        set({
          doc: { ...doc, proposals: doc.proposals ?? [], corrections: doc.corrections ?? null },
          past: [],
          future: [],
          selectedId: null,
          selectedProposalId: null,
          draft: null,
        }),
      ),

    /*
     * START THIS HOUSE OVER.
     *
     * Not the same thing as a new mockup, which is a different house. This is
     * the one a contractor asks for out loud: he has drawn the whole front,
     * decided it is wrong, and wants the lights off without losing the photo,
     * the scale he measured, or the customer's name. Doing it by deleting runs
     * one at a time is what people were reduced to.
     *
     * Snapshotted first, so it is one Undo away.
     */
    /*
     * The photo would not decode. Keep everything else.
     *
     * Not an undo step and not a commit: nothing the user did caused this, and
     * putting it in the history would let them undo their way back to a broken
     * job. The runs stay, because they are stored as fractions of the photo and
     * will land exactly where they were when a photo is put back.
     */
    dropPhoto: () => set((s) => (s.doc.photo ? { doc: { ...s.doc, photo: null } } : {})),

    clearRuns: () => {
      commit()
      set((s) => ({
        doc: {
          ...s.doc,
          layers: [],
          // Proposals are runs too. Leaving them would clear the lights off
          // the house and leave the dashed lines behind.
          proposals: [],
          /*
           * BACK TO DAYLIGHT TOO.
           *
           * Clearing the runs on a night render left a dark house with nothing
           * on it, which reads as the app having broken rather than as a blank
           * sheet - reported as "it just refreshes everything and there is a
           * black screen". You design on the daytime photo and turn it to
           * night at the end, so starting over is starting over on the photo
           * you took.
           */
          scene: { ...s.doc.scene, night: 0 },
        },
        selectedId: null,
        selectedProposalId: null,
        draft: null,
        hover: null,
      }))
    },

    newDocument: () =>
      set({
        doc: freshDocument(uid()),
        past: [],
        future: [],
        selectedId: null,
        selectedProposalId: null,
        draft: null,
        edgeMap: null,
        calibDraft: { a: null, b: null },
        // A new job starts with nothing armed, for the same reason opening the
        // app does: the first press should not be the start of something
        // nobody asked for. The category goes back to its own default too, so
        // Start over does not quietly leave you in Wreaths.
        tool: 'select',
        category: 'roofline',
        // And back to trunks, so a new palm does not open in fronds because
        // the last house ended there.
        palmMode: 'trunk',
        /*
         * AND THE AI FORGETS THE LAST JOB.
         *
         * suggest.generation is what makes the button read "Roof again" and
         * what a regeneration replaces. It survived Start over, so a brand new
         * job opened claiming a generation that belonged to the house before
         * it - and, worse, a failure carried over too, so a fresh job could
         * open already in a failed state. Reported as: every time I open and I
         * hit this it says this, as if I already did it, when it starts fresh
         * it should start fresh.
         *
         * There is nothing to replace in an empty document, which is exactly
         * what idle means.
         */
        suggest: { phase: 'idle', message: null, added: 0, generation: null, replaceable: 0, confirming: false },
      }),

    /* --------------------------------------------- the confirmation layer */

    selectProposal: (selectedProposalId) =>
      set({ selectedProposalId, selectedId: null }),

    /*
     * SUGGESTIONS ARRIVE LIT, LIKE EVERYTHING ELSE.
     *
     * They used to arrive dashed and dark and wait for Include. The rule that
     * justified it was real: geometry nobody agreed to must not become footage
     * on an invoice. But the app is no longer the thing that prices a job.
     * Pricing is a short list by category that a person types, so there is no
     * computed number left for a wrong line to corrupt, and the step was
     * costing a press per run to protect a quantity that no longer exists.
     *
     * What survives is what actually matters to somebody standing in a
     * driveway. Every run is still individually removable, moving one still
     * withdraws and re-derives it, and undo steps back over a whole generation
     * rather than one line at a time - so a bad AI roof is one press to
     * clear rather than four.
     *
     * IF PRICING EVER GOES BACK TO COMPUTING FOOTAGE PER RUN, THIS DECISION
     * HAS TO BE REVISITED. That is the condition it rests on and it is written
     * here rather than remembered.
     */
    addProposals: (list) => {
      if (!list.length) return
      const template = { ...get().defaults }
      withHistory((doc) => addLit(doc, list, template), { selectedProposalId: null })
    },

    /* ------------------------------------------------- ai suggestions */

    suggest: { phase: 'idle', message: null, added: 0, generation: null, replaceable: 0, confirming: false },
    accessCode: readAccessCode(),

    setAccessCode: (code) => {
      const trimmed = code.trim()
      set({ accessCode: trimmed })
      /*
       * sessionStorage, not the document and not localStorage. The code is a
       * shared trial secret: it must not travel inside a saved project, must
       * not survive the tab, and must never be written into an export that
       * gets forwarded around a neighborhood.
       */
      try {
        if (trimmed) sessionStorage.setItem(ACCESS_CODE_KEY, trimmed)
        else sessionStorage.removeItem(ACCESS_CODE_KEY)
      } catch {
        // Private browsing. The code simply lives in memory for this session.
      }
    },

    dismissSuggestMessage: () =>
      set((s) => ({ suggest: { ...s.suggest, message: null, phase: 'idle' } })),

    setSuggestConfirming: (confirming) => set((s) => ({ suggest: { ...s.suggest, confirming } })),

    cancelSuggestions: () => {
      /*
       * HONEST ABOUT WHAT THIS DOES. It stops the waiting and it guarantees
       * nothing will be imported. A request already running at OpenAI may
       * still finish, and if it does it is still billed. See
       * docs/AI-SUGGESTIONS-ENDPOINT.md.
       */
      suggestAbort?.abort()
    },

    generateSuggestions: async (seam) => {
      const st = get()
      // One at a time. A second press while one is running is not a second
      // job, because a second job is a second six cents and a second upload.
      if (st.suggest.phase === 'uploading' || st.suggest.phase === 'analyzing') return

      const keep = (patch: Partial<typeof st.suggest>) =>
        set((s) => ({ suggest: { ...s.suggest, confirming: false, ...patch } }))

      if (!st.doc.photo) {
        keep({ phase: 'failed', message: CLIENT_MESSAGES.no_photo, added: 0 })
        return
      }
      if (!st.accessCode) {
        keep({ phase: 'failed', message: 'Enter your access code first.', added: 0 })
        return
      }

      /*
       * A REGENERATION IS A REPLACEMENT, AND IT IS DECIDED BEFORE THE CALL.
       *
       * The generation being replaced is captured here, not after the answer
       * comes back, so that what gets removed is exactly what the person was
       * told would be removed - not whatever happens to be on screen a minute
       * later. If the call fails or is cancelled, this is simply never used.
       */
      const replacing = st.suggest.generation
      const controller = new AbortController()
      suggestAbort = controller
      keep({ phase: 'uploading', message: null, added: 0 })

      const outcome = await requestSuggestions({
        // The ORIGINAL photo, read only. Nothing in this action writes to
        // doc.photo, and the analysis copy the client may make from it is
        // transient: it is never stored, drawn, saved or exported.
        dataUrl: st.doc.photo.dataUrl,
        photoWidth: st.doc.photo.width,
        photoHeight: st.doc.photo.height,
        accessCode: st.accessCode,
        signal: controller.signal,
        onProgress: ({ phase }) => {
          // Only the two waiting phases; the ending is set from the outcome.
          if (phase === 'uploading' || phase === 'analyzing') {
            set((s) => ({ suggest: { ...s.suggest, phase } }))
          }
        },
        ...seam,
      })
      suggestAbort = null

      /*
       * EVERY FAILURE LEAVES THE DESIGN EXACTLY AS IT WAS.
       *
       * There is exactly one withHistory call in this whole function and it is
       * on the ok branch below. Nothing on this branch touches doc, so a
       * refusal, a timeout, a cancel and an unreadable response cannot remove
       * the previous generation's suggestions either: a failed replacement
       * leaves the thing it was going to replace.
       */
      if (!outcome.ok) {
        /*
         * A CODE THE SERVER NO LONGER ACCEPTS IS WORSE THAN NO CODE.
         *
         * The code is stored on the way in from the login and reused for every
         * call after. Rotate it - which had to happen once, because it was
         * published in a test fixture - and the stored one keeps being sent,
         * keeps being refused, and the button just fails forever with nothing
         * to act on. Reported as: the AI roof is not working anymore.
         *
         * So a refusal throws the stored code away and says where to get a new
         * one. The next visit through the login supplies it, and the failure
         * becomes a thing somebody can do something about instead of a wall.
         */
        if (outcome.failure === 'not_authorised') {
          get().setAccessCode('')
          keep({
            phase: 'failed',
            added: 0,
            message: `That sign in has expired. Sign in again at ${SITE_HOST} and reopen the app.`,
          })
          return
        }
        keep({
          phase: outcome.cancelled ? 'cancelled' : 'failed',
          message: outcome.message,
          added: 0,
        })
        return
      }

      /*
       * ONE UPDATE: REMOVE, THEN ADD.
       *
       * Both halves are inside a single withHistory, so there is no frame in
       * which the old suggestions are gone and the new ones have not arrived,
       * and one undo puts the design back exactly as it was.
       *
       * WHAT IS REMOVED is narrow on purpose: a proposal is swept up only if
       * it carries the previous generation's tag AND nobody has touched it.
       *
       *   a run drawn by hand      has no tag, so it cannot match
       *   a run you moved          has a geometryRev above zero, so it stays,
       *                            and so does the Strand it owns
       *   an older generation      carries a different tag, so a suggestion
       *                            someone deliberately kept survives
       *
       * THE TEST USED TO BE `status === 'proposed'`, and that stopped meaning
       * what it said the moment suggestions started arriving lit. Everything
       * became confirmed on arrival, so nothing matched and a second press
       * stacked a whole generation on top of the first; worse, moving a run
       * withdraws its confirmation, so 'proposed' had come to mean the exact
       * set of runs the person had worked on - it would have swept up their
       * corrections and left the model's guesses standing.
       *
       * The edit counter says what status can no longer say. It is incremented
       * by every edit and by nothing else, and it is reset to zero on arrival
       * just below, because the counter belongs to this app and a response
       * from the network does not get to seed it.
       *
       * WHAT IS ADDED cannot duplicate what is there: the server namespaces
       * every proposal id with its own request id, so no two generations can
       * emit the same id, and the removal above has already taken out the
       * batch this one supersedes.
       */
      const generation = outcome.requestId
      const tagged = outcome.proposals.map((p) => ({
        ...p,
        aiGeneration: generation,
        geometryRev: 0,
      }))
      const template = { ...st.defaults }

      withHistory(
        (doc) => {
          const superseded = doc.proposals.filter(
            (p) => !!replacing && p.aiGeneration === replacing && p.geometryRev === 0,
          )
          // removeProposal takes the Strand with it and counts the rejection,
          // so a lit run that is being replaced does not leave its bulbs on
          // the roof.
          let next = doc
          for (const p of superseded) next = removeProposalDoc(next, p.id)
          return addLit(next, tagged, template)
        },
        { selectedProposalId: null },
      )

      keep({
        phase: 'ready',
        added: tagged.length,
        generation,
        replaceable: tagged.length,
        message:
          tagged.length === 0
            ? 'No roof runs were found in this photo. Draw one yourself.'
            : null,
      })
    },

    confirmRun: (id) => {
      const { doc, defaults } = get()
      const p = doc.proposals.find((q) => q.id === id)
      /*
       * A CONFIRMED RUN IS MADE OF WHAT HE IS HOLDING.
       *
       * This used to pass no template at all, so every confirmed run came back
       * as the built-in C9 warm white - which happens to equal the factory
       * defaults, so it looked right until anybody changed anything, and then
       * picking Red and confirming gave you warm white. Not merely cosmetic:
       * product decides how the run is ORDERED, and a contractor working in
       * minis had his roof counted as spool footage.
       */
      const r = confirmProposalDoc(doc, id, {
        now: new Date().toISOString(),
        strandId: uid(),
        template: { ...defaults },
        name: p ? runLabel(p, doc.proposals.indexOf(p)) : 'Roof run',
      })
      if (!r.ok) return { ok: false, blockers: r.blockers }
      if (r.noop) return { ok: true, blockers: [] }
      // Confirming is the end of a decision, not the start of a drawing.
      withHistory(() => r.doc, { tool: 'select' as Tool })
      return { ok: true, blockers: [] }
    },

    /*
     * Moving a run withdraws its confirmation. No threshold.
     *
     * A one bulb tolerance was considered and rejected: the question a
     * confirmation answers is "is this where the lights go", and a run that
     * has moved at all is a run nobody has answered that question about. The
     * tolerance would also be the exact size of the mistake it was meant to
     * forgive - a lane error is a few pixels.
     */
    editRunPoints: (id, points) => {
      withHistory((doc) => {
        const out = editProposal(doc, id, { points })
        return relight(doc, countCorrection(out.doc, { runsEdited: 1, circlesMoved: 1 }), id)
      })
    },

    beginRunEdit: () => commit(),

    editRunPointsLive: (id, points) =>
      set((s) => ({ doc: relight(s.doc, editProposal(s.doc, id, { points }).doc, id) })),

    setSideReturn: (id, which, decision) => {
      withHistory((doc) => {
        const out = editProposal(doc, id, (p) => ({
          ends: {
            ...p.ends,
            [which]: { ...p.ends[which], sideReturnDecision: decision, decidedAt: new Date().toISOString() },
          } as RoofProposal['ends'],
        }))
        return relight(doc, countCorrection(out.doc, {
          sideReturnTrimmed: decision === 'trim' ? 1 : 0,
          sideReturnIncluded: decision === 'include' ? 1 : 0,
        }), id)
      })
    },

    setOcclusion: (id, spanIndex, decision) => {
      withHistory((doc) => {
        const out = editProposal(doc, id, (p) => ({
          spans: p.spans.map((sp, i) =>
            i === spanIndex ? { ...sp, decision, decidedAt: new Date().toISOString() } : sp,
          ),
        }))
        return relight(doc, countCorrection(out.doc, {
          occludedSpansConfirmed: decision === 'continues' ? 1 : 0,
          occludedSpansExcluded: decision === 'exclude' ? 1 : 0,
          occludedSpansLeftUnresolved: decision === 'unresolved' ? 1 : 0,
        }), id)
      })
    },

    removeRun: (id) => {
      withHistory((doc) => removeProposalDoc(doc, id), {
        selectedProposalId: null,
      })
    },

    /* ----------------------------------------------- places on the house */

    addSection: (patch) => {
      const made = createSection(patch)
      withHistory((doc) => ({ ...doc, sections: [...sectionsOf(doc), made] }))
      return made.id
    },

    /**
     * Every one of these edits by id and rebuilds the array. None of them
     * reads a position, and the label is only ever a value - which is the
     * whole reason a section can be renamed in December without breaking a
     * reservation made in September.
     */
    renameSection: (id, label) => patchSection(id, () => ({ label })),

    setSectionKind: (id, kind) => patchSection(id, () => ({ kind })),

    setSectionExcluded: (id, excluded, reason) =>
      patchSection(id, () => ({ excluded, excludedReason: excluded ? (reason ?? '') : '' })),

    removeSection: (id) => {
      withHistory((doc) => ({
        ...doc,
        sections: sectionsOf(doc).filter((x) => x.id !== id),
      }))
    },

    attachRunToSection: (sectionId, runId) => {
      withHistory((doc) => ({ ...doc, sections: attachRunTo(sectionsOf(doc), sectionId, runId) }))
    },

    detachRunFromSection: (runId) => {
      withHistory((doc) => ({ ...doc, sections: detachRunFrom(sectionsOf(doc), runId) }))
    },

    estimateSection: (id, input) =>
      patchSection(id, (sec) =>
        setMeasurement(sec, {
          feet: input.feet,
          source: input.source,
          plusMinus: input.plusMinus,
          reason: input.reason,
          at: new Date().toISOString(),
        }),
      ),

    verifySection: (id, input) =>
      patchSection(id, (sec) =>
        verifyMeasurement(sec, {
          feet: input.feet,
          by: input.by,
          evidence: input.evidence,
          reason: input.reason,
          at: new Date().toISOString(),
        }),
      ),

    approveSections: ({ by, note }) => {
      const st = get()
      const sections = sectionsOf(st.doc)
      const blockers = approvalBlockers(sections)
      if (blockers.length) return { ok: false, blockers }

      /*
       * The counts are frozen with the scope rather than recomputed later,
       * because a recipe or a default product can change between September and
       * a February repair, and the question "what did we agree" must not
       * quietly get a new answer when it does.
       */
      const product = PRODUCTS[st.defaults.product] ?? PRODUCTS.C9
      const t = takeoff(sections, product.spacingInches)
      /*
       * Which package this scope is, recorded by name as well as by id. A
       * package list can be renamed or reordered later; what a customer agreed
       * to was called something at the time, and that is what a person reading
       * the approval in February needs to see.
       */
      const pkg = PACKAGES.find((p) => p.product === st.defaults.product)
      const scope = approveScope(sections, t, {
        by,
        note,
        packageId: pkg?.id,
        packageName: pkg?.name,
        at: new Date().toISOString(),
      })

      withHistory((doc) => ({ ...doc, approvals: [...approvalsOf(doc), scope] }))
      return { ok: true, blockers: [] }
    },

    undo: () => {
      const { past, doc, future } = get()
      if (!past.length) return
      const prev = past[past.length - 1]
      set({
        doc: prev,
        past: past.slice(0, -1),
        future: [cloneDoc(doc), ...future.slice(0, 49)],
        selectedId: null,
        // A proposal id from the other side of an undo may not exist any more,
        // and an edit aimed at a run that is gone is how a stale Strand gets
        // left behind.
        selectedProposalId: null,
      })
    },

    redo: () => {
      const { future, doc, past } = get()
      if (!future.length) return
      const next = future[0]
      set({
        doc: next,
        future: future.slice(1),
        past: [...past.slice(-49), cloneDoc(doc)],
        selectedId: null,
        selectedProposalId: null,
      })
    },
  }
})

function cloneLayer(l: Layer): Layer {
  return typeof structuredClone === 'function' ? structuredClone(l) : JSON.parse(JSON.stringify(l))
}

/** Selector helpers. */
export const selectedStrand = (s: EditorState): Strand | null => {
  const l = s.doc.layers.find((x) => x.id === s.selectedId)
  return l && isStrand(l) ? l : null
}
